import React from 'react';
import dayjs from 'dayjs';
import { formatMoney, matchedServiceLine } from '../../Utils/stediUtils';
import { MUTED, PANEL_STYLE, TITLE } from './StediResultParts';

// Shared pieces of the Claim 360 view pages (eligibility, denial management).

export const STATUS_STYLE = {
    success: { backgroundColor: 'var(--hc-status-success-bg)', color: 'var(--hc-status-success-text)' },
    danger: { backgroundColor: 'var(--hc-status-danger-bg)', color: 'var(--hc-status-danger-text)' },
    warning: { backgroundColor: 'var(--hc-status-warning-bg)', color: 'var(--hc-status-warning-text)' },
};

export const formatDateTime = (value) => (value && dayjs(value).isValid() ? dayjs(value).format('MMM D, YYYY h:mm A') : '-');

export function Section({ icon: Icon, title, aside, children, className = '' }) {
    return (
        <section className={`p-3 p-md-4 hc-surface hc-card-primary ${className}`}>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <h5 className="fw-bold mb-0 d-flex align-items-center gap-2 hc-text-primary">
                    <Icon size={22} /> {title}
                </h5>
                {aside}
            </div>
            {children}
        </section>
    );
}

// [label, value] pairs as small cards, two per row.
// The service line of a claim status check run with a procedure code: its status and amounts, or a note that the
// payer listed no such line.
export function ServiceLineFields({ response, procedureCode, chargeAmount, colClass }) {
    const match = matchedServiceLine(response, procedureCode, chargeAmount);
    if (!match) {
        // Many payers answer at claim level only: say so, rather than suggest the line is missing from the claim.
        const hasLines = (response?.claims ?? []).some((claim) => claim?.serviceDetails?.length);
        return (
            <div className="small" style={MUTED}>
                {hasLines
                    ? `${response?.payer?.organizationName || 'The payer'} listed lines on this claim, but none for ${procedureCode}${chargeAmount ? ` billed at ${formatMoney(chargeAmount)}` : ''}; the claim status above applies.`
                    : `${response?.payer?.organizationName || 'The payer'} gave no line-level detail for ${procedureCode}; the claim status above applies to it.`}
            </div>
        );
    }
    const { service = {}, status = [] } = match.line;
    return (
        <FieldGrid
            colClass={colClass}
            fields={[
                ['Procedure Code', [service.procedureId, ...(service.procedureModifiers ?? [])].filter(Boolean).join(' - ')],
                ['Line Status', status.map((entry) => [entry.statusCategoryCodeValue, entry.statusCodeValue].filter(Boolean).join(' - ')).filter(Boolean).join('; ')],
                ['Billed', formatMoney(service.submittedAmount)],
                ['Paid', formatMoney(service.amountPaid)],
                ['Units', service.submittedUnits],
                ['Payer Claim #', match.claim.claimStatus?.tradingPartnerClaimNumber],
            ].filter(([, value]) => value)}
        />
    );
}

export function FieldGrid({ fields, colClass = 'col-12 col-sm-6' }) {
    if (!fields.length) return <div className="small" style={MUTED}>Nothing returned.</div>;
    return (
        <div className="row g-2 g-md-3">
            {fields.map(([label, value]) => (
                <div className={colClass} key={label}>
                    <div className="h-100 rounded-3 border px-3 py-2" style={PANEL_STYLE}>
                        <div className="small" style={MUTED}>{label}</div>
                        <div className="fw-semibold text-break" style={TITLE}>{value || '-'}</div>
                    </div>
                </div>
            ))}
        </div>
    );
}

export function SummaryTile({ icon: Icon, label, value, hint }) {
    return (
        <div className="col-12 col-sm-6 col-xl-3">
            <div className="h-100 rounded-4 border p-3 d-flex align-items-center gap-3" style={PANEL_STYLE}>
                <span
                    className="d-inline-flex align-items-center justify-content-center rounded-3 hc-bg-primary text-white flex-shrink-0"
                    style={{ width: 44, height: 44 }}
                >
                    <Icon size={22} />
                </span>
                <div style={{ minWidth: 0 }}>
                    <div className="small fw-semibold" style={MUTED}>{label}</div>
                    <div className={value ? 'fw-bold fs-5 lh-sm text-break' : 'fw-semibold fst-italic'} style={value ? TITLE : { color: 'var(--hc-text-faint)' }}>
                        {value || 'Not returned'}
                    </div>
                    {hint && <div className="small" style={{ color: 'var(--hc-text-faint)' }}>{hint}</div>}
                </div>
            </div>
        </div>
    );
}
